import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

const PermissionContext = createContext(null);

export function PermissionProvider({ children }) {
    const { user } = useAuth();
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchPermissions = useCallback(async () => {
        if (!user) {
            setPermissions([]);
            return;
        }

        try {
            setLoading(true);
            const response = await api.get(`/users/${user.id}/permissions`);
            // effective_permissions contains both role and special permissions
            setPermissions(response.data.effective_permissions || []);
        } catch (err) {
            console.error('Error fetching permissions:', err);
            setPermissions([]);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (user) {
            fetchPermissions();
        } else {
            setPermissions([]);
        }
    }, [user, fetchPermissions]);

    // Check if user has a specific permission
    const hasPermission = useCallback((permission) => {
        // Super admin has all permissions
        if (user?.role === 'super_admin' || user?.role === 'admin') {
            return true;
        }
        return permissions.includes(permission);
    }, [user, permissions]);

    // Check if user has any of the given permissions
    const hasAnyPermission = useCallback((...requiredPermissions) => {
        if (user?.role === 'super_admin' || user?.role === 'admin') {
            return true;
        }
        return requiredPermissions.some(p => permissions.includes(p));
    }, [user, permissions]);

    // Check if user has all of the given permissions
    const hasAllPermissions = useCallback((...requiredPermissions) => {
        if (user?.role === 'super_admin' || user?.role === 'admin') {
            return true;
        }
        return requiredPermissions.every(p => permissions.includes(p));
    }, [user, permissions]);

    // Refresh permissions (call after role change)
    const refreshPermissions = useCallback(() => {
        fetchPermissions();
    }, [fetchPermissions]);

    const value = {
        permissions,
        loading,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        refreshPermissions
    };

    return (
        <PermissionContext.Provider value={value}>
            {children}
        </PermissionContext.Provider>
    );
}

export function usePermissions() {
    const context = useContext(PermissionContext);
    if (!context) {
        throw new Error('usePermissions must be used within a PermissionProvider');
    }
    return context;
}

// HOC for protected components
export function withPermission(WrappedComponent, requiredPermission, Fallback = null) {
    return function PermissionWrapper(props) {
        const { hasPermission, loading } = usePermissions();

        if (loading) {
            return null;
        }

        if (!hasPermission(requiredPermission)) {
            return Fallback;
        }

        return <WrappedComponent {...props} />;
    };
}

// Component for conditional rendering based on permission
export function Can({ permission, permissions, all = false, children, fallback = null }) {
    const { hasPermission, hasAnyPermission, hasAllPermissions, loading } = usePermissions();

    if (loading) {
        return null;
    }

    // Single permission check
    if (permission) {
        return hasPermission(permission) ? children : fallback;
    }

    // Multiple permissions check
    if (permissions && permissions.length > 0) {
        const hasAccess = all
            ? hasAllPermissions(...permissions)
            : hasAnyPermission(...permissions);
        return hasAccess ? children : fallback;
    }

    return fallback;
}
